import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Button, Icon, Menu, TableCell, useDisclosure } from '@percent/lemonade'
import { JsonViewToggle } from '@percent/partner-dashboard/common/components/drawer/JsonViewToggle'
import type { JsonViewToggleProps } from '@percent/partner-dashboard/common/components/drawer/JsonViewToggle.types'

export type CategoryNonprofitActionsMenuProps = Readonly<{
  organisationId: string
  organisationName: string
  rawJsonData: JsonViewToggleProps['rawJsonData']
  onRemove?: (organisation: { id: string; name: string }) => void
}>

export function CategoryNonprofitActionsMenu({
  organisationId,
  organisationName,
  rawJsonData,
  onRemove
}: CategoryNonprofitActionsMenuProps) {
  const { t } = useTranslation()
  const [isPreviewOpened, { open: openPreview, close: closePreview }] = useDisclosure(false)

  const items = useMemo(
    () => [
      { key: 'view_raw', label: t('button.viewRaw'), icon: 'view-on' as const },
      ...(onRemove
        ? [{ key: 'remove', label: t('categories.removeOrganisation.menuItem'), icon: 'delete' as const }]
        : [])
    ],
    [t, onRemove]
  )

  const onSelect = (key: string) => {
    if (key === 'view_raw') {
      openPreview()
    }

    if (key === 'remove') {
      onRemove?.({ id: organisationId, name: organisationName })
    }
  }

  return (
    <TableCell ta="right" data-testid="category-nonprofit-actions-cell">
      <Menu
        customTriggerComponent={
          <Button variant="tertiary" size="small" data-testid={`category-nonprofit-actions-${organisationId}`}>
            <Icon name="menu-vertical" size={6} />
          </Button>
        }
        onSelect={onSelect}
        sections={[{ items }]}
      />
      <JsonViewToggle rawJsonData={rawJsonData} isOpen={isPreviewOpened} onOpen={openPreview} onClose={closePreview} />
    </TableCell>
  )
}
